import {
    useId,
    ReactElement,
    useState,
    useEffect,
    useRef,
    useCallback,
    forwardRef,
    useImperativeHandle
} from 'react';
import styles from './ui-phone-input.module.scss';
import { useUITranslation } from '../../hooks';
import { Link } from 'react-router';
import { UIText } from '../ui-text';
import { UIPopover, UIPropoverRef } from '../ui-popover';
import { UIMenu, UIMenuItem } from '../ui-menu';
import callingCodes from './international-calling-codes.json';
import { UISearchInput } from '../ui-search-input';
import { validatePhone } from '../../util/validate-phone';
import { UIIcon, UITextButton } from '../..';

type CallingCode = {
    name: string;
    isoCode: string;
    dialCode: string;
};

export type UIPhoneInputProps = {
    name: string;
    label?: string;
    labelButton?: {
        text: string;
        link: string;
    };
    initialDialCode: string;
    initialNumber: string;
    placeholder?: string;
    description?: string;
    required?: boolean;
    disabled?: boolean;
    autoFocus?: boolean;
    showRealTimeErrors?: boolean;
    style?: React.CSSProperties;
    /**
     * When set, forces the dial code to this value and hides the country
     * dropdown. Used to restrict input to a single region (e.g. '+1' when
     * the app's resolved region is 'us').
     */
    lockDialCode?: string;
    onChange?: (dialCode: string, number: string, isValid: boolean) => void;
};

export type UIPhoneInputRef = {
    resetForm: () => void;
};

// export function UIPhoneInput(props: UIPhoneInputProps): ReactElement {
export const UIPhoneInput = forwardRef<UIPhoneInputRef, UIPhoneInputProps>(
    (props, ref): ReactElement => {
        const { t } = useUITranslation();
        const {
            name,
            label,
            labelButton,
            initialDialCode,
            initialNumber,
            placeholder,
            description = '',
            required = false,
            disabled = false,
            autoFocus = false,
            showRealTimeErrors = false,
            style,
            lockDialCode,
            onChange
        } = props;

        const id = useId();

        const popoverRef = useRef<UIPropoverRef>(null);
        const [query, setQuery] = useState<string>('');
        const [filteredData, setFilteredData] =
            useState<CallingCode[]>(callingCodes);

        useEffect(() => {
            const queryTrimmedAndLower: string = query.trim().toLowerCase();

            const filteredCallingCodes = callingCodes.filter(
                (item: CallingCode) => {
                    return (
                        item.name
                            .trim()
                            .toLowerCase()
                            .includes(queryTrimmedAndLower) ||
                        item.isoCode
                            .trim()
                            .toLowerCase()
                            .includes(queryTrimmedAndLower) ||
                        item.dialCode
                            .trim()
                            .toLowerCase()
                            .includes(queryTrimmedAndLower)
                    );
                }
            );
            setFilteredData(filteredCallingCodes);
        }, [query]);

        /**
         * Phone number
         */
        const [dialCode, setDialCode] = useState<string>(
            lockDialCode ?? initialDialCode
        );
        const [number, setNumber] = useState(initialNumber);

        useEffect(() => {
            if (lockDialCode && lockDialCode !== dialCode) {
                setDialCode(lockDialCode);
            }
        }, [lockDialCode, dialCode]);
        const [phoneError, setPhoneError] = useState('');
        const checkPhone = useCallback(
            (phone: string): string => {
                const error = validatePhone(phone);
                if (error) {
                    return t(error);
                }
                return '';
            },
            [t]
        );
        useEffect(() => {
            const phoneError = checkPhone(dialCode.trim() + number.trim());
            if (showRealTimeErrors) {
                setPhoneError(phoneError);
            }

            onChange?.(dialCode, number, !phoneError);
        }, [dialCode, number, showRealTimeErrors, checkPhone, onChange]);

        const inputClasses = [styles.inputContainer];
        if (phoneError) {
            inputClasses.push(styles.inputError);
        }
        if (disabled) {
            inputClasses.push(styles.inputDisabled);
        }

        function resetForm() {
            setDialCode(lockDialCode ?? initialDialCode);
            setNumber(initialNumber);
            setPhoneError('');
        }

        useImperativeHandle(ref, () => ({
            resetForm() {
                resetForm();
            }
        }));
        return (
            <div className={styles.container} style={style}>
                {label && (
                    <div className={styles.labelContainer}>
                        <div>
                            <label className={styles.label} htmlFor={id}>
                                {label}
                            </label>
                            {required ? (
                                <label
                                    className={`${styles.label} ${styles.labelRequired}`}
                                    htmlFor={id}
                                >
                                    {' *'}
                                </label>
                            ) : undefined}
                        </div>

                        {labelButton && labelButton.link && (
                            <Link
                                to={labelButton.link}
                                style={{ textDecoration: 'none' }}
                            >
                                <UITextButton variant="primary" type="button">
                                    {labelButton.text}
                                </UITextButton>
                            </Link>
                        )}
                    </div>
                )}

                <div className={inputClasses.join(' ')}>
                    {!disabled && lockDialCode && (
                        <div
                            className={styles.dialCodeContainer}
                            style={{ cursor: 'default' }}
                        >
                            {dialCode}
                        </div>
                    )}
                    {!disabled && !lockDialCode && (
                        <UIPopover
                            ref={popoverRef}
                            popoverPadding={0}
                            triggerNode={
                                <button
                                    className={`${styles.dialCodeContainer} ${disabled ? styles.dialCodeContainerDisabled : ''}`}
                                    type="button"
                                >
                                    <>
                                        {dialCode}
                                        <UIIcon name="chevron-down" size={16} />
                                    </>
                                </button>
                            }
                        >
                            <UIMenu
                                scroll
                                maxHeight={250}
                                header={
                                    <UISearchInput
                                        value={query}
                                        placeholder={t('shared.labels.search')}
                                        onChange={setQuery}
                                    />
                                }
                            >
                                {filteredData.map((item, index) => (
                                    <UIMenuItem
                                        key={index}
                                        text={`${item.name} (${item.dialCode})`}
                                        onClick={() => {
                                            setDialCode(item.dialCode);
                                            popoverRef.current?.close();
                                        }}
                                    />
                                ))}
                            </UIMenu>
                        </UIPopover>
                    )}
                    <input
                        className={styles.input}
                        type={'text'}
                        id={id}
                        value={
                            disabled
                                ? `${initialDialCode}${initialNumber}`
                                : number
                        }
                        name={name}
                        placeholder={placeholder}
                        required={required}
                        disabled={disabled}
                        aria-invalid={!!phoneError}
                        autoFocus={autoFocus}
                        onChange={(e) => {
                            const numbersAndSpacesOnly = e.target.value.replace(
                                /[^0-9 ]/g,
                                ''
                            );
                            setNumber(numbersAndSpacesOnly);
                        }}
                    />
                </div>
                {phoneError && (
                    <div className={styles.errorTextContainer}>
                        <UIText variant="small" type="error">
                            {phoneError}
                        </UIText>
                    </div>
                )}
                {!phoneError && description && (
                    <div className={styles.descriptionTextContainer}>
                        <UIText variant="small">{description}</UIText>
                    </div>
                )}
            </div>
        );
    }
);
